import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { CitationDrawer } from '@insightai/ai-patterns'
import { ApiError, staleTimes } from '@insightai/api-client'
import { initI18n } from '@insightai/i18n'
import { MotionProvider, ThemeProvider, ToastProvider, TooltipProvider } from '@insightai/ui'
import { useState, type ReactNode } from 'react'
import { api } from './session'

initI18n()

/**
 * Query defaults.
 *
 * `retry` deliberately excludes 4xx. Retrying a 403 three times turns one honest
 * permission error into a three-second hang plus three audit-log entries, and
 * retrying a 422 will never succeed.
 */
function createQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: staleTimes.default,
        retry: (failureCount, error) => {
          if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false
          return failureCount < 2
        },
        refetchOnWindowFocus: true,
      },
      mutations: { retry: false },
    },
  })
}

export function AppProviders({ children }: { children: ReactNode }) {
  const [queryClient] = useState(createQueryClient)

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider
        onPersist={(theme) => {
          // DB-FR-008: the cookie is the immediate effect; the profile is the
          // durable record, so the same choice follows the user to a new device.
          void api.PATCH('/api/me/preferences', { body: { theme_preference: theme } })
        }}
      >
        <MotionProvider>
          <TooltipProvider>
            <ToastProvider>
              {children}
              {/* §5.2 — ONE citation drawer for the whole app, driven by a store. */}
              <CitationDrawer />
            </ToastProvider>
          </TooltipProvider>
        </MotionProvider>
      </ThemeProvider>
    </QueryClientProvider>
  )
}

export { createQueryClient }
